import { INumericIdentifierModel, IUpdatedAtAndBy } from "@api/schema"

import { DocumentTemplateCategoryName } from "./categories"
import { IDocument } from "./documents"

/**
 * Structure of the template data stored for a chapter.
 */
export interface IChapterTemplateDto {
  /**
   * Backend set type of the IChapter, e.g. "ChapterTemplateDto_v1"
   */
  "@type"?: string
  /**
   * Name of the chapter template, which is originally used to create the chapter.
   * May be empty, but only if the document template is empty too.
   */
  chapter: string
  /**
   * Name of the document template, to which the chapter belongs to.
   * May be empty, but only if the chapter template is empty too.
   */
  document: DocumentTemplateCategoryName
}

/**
 * Structure of a Chapter
 */
export interface IChapter<Document extends IDocument = IDocument> extends INumericIdentifierModel, IUpdatedAtAndBy {
  /**
   * Template data, a chapter is based on.
   */
  template?: IChapterTemplateDto
  /**
   * Title of the chapter.
   */
  title?: string
  /**
   * Content of the chapter.
   * May contain HTML.
   */
  content?: string
  /**
   * Checklist to mark, what aspects of a chapter is finished.
   */
  checklist?: IChapterChecklistDto[]
  /**
   * Parent document, to which the chapter belongs.
   */
  document?: Document
  /**
   * Order position of the chapter within the list of all chapters in the document.
   */
  sortOrder?: number
}

/**
 * States, a Checklist item may have.
 *
 * NOTE: constants are defined by the backend, do not change without changing them there!
 */
export enum ChecklistItemState {
  /** Task is open, is not done yet (unchecked). */
  Open = "open",
  /** Task is done (checked). */
  Done = "done",
  /** Task is to be ignored, e.g. because it is not relevant. */
  Ignored = "ignored",
}

/**
 * Current backend type name of the checklist DTO.
 */
export const CURRENT_CHAPTER_CHECKLIST_TYPE = 'ChapterChecklistDto_v1'

/**
 * Current backend type name of the chapter template DTO.
 */
export const CURRENT_CHAPTER_TEMPLATE_TYPE = "ChapterTemplateDto_v1"

/**
 * Structure of a user defined checklist item.
 */
export interface IChapterChecklistDto {
  /**
   * Backend set type of the IChapterChecklist, e.g. "ChapterChecklistDto_v1"
   */
  '@type': string
  /**
   * Description of the checklist item
   */
  task: string
  /**
   * Type of the checklist item:
   * Is it defined by the user or by the used template?
   */
  type: "user" | "template"
  /**
   * State of the checklist item: Is it done or not?
   */
  state: ChecklistItemState
}